import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Input,
  Text,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  HStack,
  Flex,
  Checkbox,
} from "@chakra-ui/react";
import axios from "axios";

/**
 * A small palette of colors to use for each unique class.
 * You can expand this if you expect more classes.
 */
const COLOR_PALETTE = [
  "red",
  "green",
  "blue",
  "orange",
  "purple",
  "magenta",
  "cyan",
  "brown",
];

// ==== เพิ่มไว้ด้านบนใกล้ๆ COLOR_PALETTE ====
const FONT_SCALE = 1.0;        // ขยายตัวอักษรบนรูป (label) ให้ใหญ่ขึ้น
const FONT_BASE_DIVISOR = 40;  // เดิม 50 -> 40 = ใหญ่ขึ้น
const MIN_FONT_PX = 14;        // ฟอนต์ขั้นต่ำ

const ImageDetectionComponent = ({ apiUrl, title }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [detectionResults, setDetectionResults] = useState(null);

  // Holds the class -> color mapping, e.g. { caries: "red", calculus: "blue" }
  const [classColors, setClassColors] = useState({});

  // Holds the class -> visibility mapping, e.g. { caries: true, calculus: false }
  const [visibleClasses, setVisibleClasses] = useState({});

  // Holds the number of bounding boxes for each class, e.g. { caries: 2, calculus: 1 }
  const [classCounts, setClassCounts] = useState({});

  // Loading & error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // References for the file input and canvas
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  /**
   * Handle file selection
   */
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setDetectionResults(null); // Clear old detections
    setError(null);
  };

  /**
   * Handle upload & detection
   */
  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select an image to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("image", selectedFile);

    setIsLoading(true);
    setError(null);

    try {
      // Call /detect_all
      const response = await axios.post(apiUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-API-KEY": `${process.env.REACT_APP_MOUTH_API_KEY}`
        }
      });
      const detections = response.data.detections || {};
      setDetectionResults(detections);

      // Collect all unique classes
      const foundClasses = new Set();
      Object.keys(detections).forEach((modelName) => {
        detections[modelName].forEach((det) => {
          foundClasses.add(det.class_name);
        });
      });

      // Update classColors for new classes
      setClassColors((prevColors) => {
        const updatedColors = { ...prevColors };
        let colorIndex = Object.keys(updatedColors).length;
        foundClasses.forEach((cls) => {
          if (!updatedColors[cls]) {
            const color =
              COLOR_PALETTE[colorIndex % COLOR_PALETTE.length] || "red";
            updatedColors[cls] = color;
            colorIndex++;
          }
        });
        return updatedColors;
      });

      // Update visibleClasses to ensure new classes default to visible
      setVisibleClasses((prevVisible) => {
        const updatedVisible = { ...prevVisible };
        let changed = false;
        foundClasses.forEach((cls) => {
          if (!(cls in updatedVisible)) {
            updatedVisible[cls] = true; // default to visible
            changed = true;
          }
        });
        return changed ? updatedVisible : prevVisible;
      });
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "An error occurred while uploading the image..."
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Re-draw the canvas whenever detectionResults, visibleClasses,
   * or the selected file changes. Also compute class counts.
   */
  useEffect(() => {
    if (!detectionResults || !selectedFile) return;

    // 1) Compute how many bounding boxes exist for each class
    const newCounts = {};
    Object.keys(detectionResults).forEach((modelName) => {
      detectionResults[modelName].forEach((det) => {
        const cls = det.class_name;
        newCounts[cls] = (newCounts[cls] || 0) + 1;
      });
    });
    setClassCounts(newCounts);

    // 2) Draw bounding boxes
    drawDetections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectionResults, visibleClasses, selectedFile]);

  /**
   * Draw bounding boxes on the canvas for classes that are currently visible
   */
  // ==== แทนที่ฟังก์ชัน drawDetections เดิม ====
const drawDetections = () => {
  const canvas = canvasRef.current;
  if (!canvas) return;
  const ctx = canvas.getContext("2d");

  const img = new Image();
  img.onload = function () {
    canvas.width = img.width;
    canvas.height = img.height;

    // วาดภาพต้นฉบับ
    ctx.drawImage(img, 0, 0);

    // คำนวณฟอนต์และความหนาเส้นตามขนาดรูป
    const base = Math.min(img.width, img.height);
    const fontSize = Math.max(
      MIN_FONT_PX,
      (base / FONT_BASE_DIVISOR) * FONT_SCALE
    );
    ctx.font = `${fontSize}px Arial`;
    ctx.lineWidth = Math.max(2, fontSize * 0.15); // หนาเทียบกับฟอนต์

    Object.keys(detectionResults).forEach((modelName) => {
      detectionResults[modelName].forEach((det) => {
        const [x1, y1, x2, y2] = det.box;
        const cls = det.class_name;

        if (!visibleClasses[cls]) return;

        const color = classColors[cls] || "red";
        ctx.strokeStyle = color;

        // กล่อง
        ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

        // พื้นหลัง label
        const label = cls;
        const textWidth = ctx.measureText(label).width;
        const padding = Math.max(4, fontSize * 0.25);
        ctx.fillStyle = color;

        // กันกรณี y1 เล็กจนชนขอบ
        const labelY = Math.max(y1, fontSize + padding + 2);

        ctx.fillRect(
          x1,
          labelY - fontSize - padding,
          textWidth + padding * 2,
          fontSize + padding
        );

        // ตัวหนังสือสีขาว
        ctx.fillStyle = "white";
        ctx.fillText(label, x1 + padding, labelY - padding);
      });
    });
  };
  img.src = URL.createObjectURL(selectedFile);
};

  /*const drawDetections = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const img = new Image();
    img.onload = function () {
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw the original image
      ctx.drawImage(img, 0, 0);

      // Determine font size based on image dimension
      const fontSize = Math.max(12, Math.min(img.width, img.height) / 50);
      ctx.font = `${fontSize}px Arial`;
      ctx.lineWidth = 2;

      Object.keys(detectionResults).forEach((modelName) => {
        detectionResults[modelName].forEach((det) => {
          const [x1, y1, x2, y2] = det.box;
          const cls = det.class_name;

          if (!visibleClasses[cls]) return;

          const color = classColors[cls] || "red";
          ctx.strokeStyle = color;

          // Draw bounding box
          ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

          // Draw label background
          const label = cls;
          const textWidth = ctx.measureText(label).width;
          const padding = 4;
          ctx.fillStyle = color;
          ctx.fillRect(
            x1,
            y1 - fontSize - padding,
            textWidth + padding * 2,
            fontSize + padding
          );

          // Draw label text in white
          ctx.fillStyle = "white";
          ctx.fillText(label, x1 + padding, y1 - padding);
        });
      });
    };
    img.src = URL.createObjectURL(selectedFile);
  };
  */

  /**
   * Toggle the visibility of a class
   */
  const toggleClassVisibility = (cls) => {
    setVisibleClasses((prev) => ({
      ...prev,
      [cls]: !prev[cls],
    }));
  };

  /**
   * Compose an export image:
   * - Left: current canvas (image + boxes)
   * - Right: white panel with "ตัวกรอง" and list "{cls} ({count})" for visible & count>0
   */
  // ==== แทนที่ฟังก์ชัน handleDownload เดิม ====
const handleDownload = () => {
  const mainCanvas = canvasRef.current;
  if (!mainCanvas) return;

  // สร้างข้อความสรุปจากตัวกรอง (แสดงเฉพาะที่ติ๊กและ count>0)
  const lines = Object.keys(visibleClasses)
    .filter((cls) => visibleClasses[cls] && (classCounts[cls] || 0) > 0)
    .map((cls) => ({
      cls,
      count: classCounts[cls],
      color: classColors[cls] || "black",
    }));

  const hasLines = lines.length > 0;

  // กำหนดความกว้าง panel ด้านขวาให้สัมพันธ์กับภาพ
  const panelWidth = Math.max(260, Math.round(mainCanvas.width * 0.30));

  // ===== upscale เพื่อความเนียนคมเวลาเซฟ (ไม่เปลี่ยนสัดส่วนฟอนต์ต่อรูป) =====
  const exportScale = 2; // ลองปรับ 1.5 ~ 3 ได้
  const exportCanvas = document.createElement("canvas");
  exportCanvas.width = (mainCanvas.width + panelWidth) * exportScale;
  exportCanvas.height = mainCanvas.height * exportScale;
  const ctx = exportCanvas.getContext("2d");

  // ใช้ระบบพิกัดเดิม แล้วคูณสเกลทั้ง canvas
  ctx.scale(exportScale, exportScale);

  // พื้นหลังขาว
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, exportCanvas.width / exportScale, exportCanvas.height / exportScale);

  // วาดภาพหลัก (รวมกรอบ/ตัวอักษรที่ render แล้วบน mainCanvas)
  ctx.drawImage(mainCanvas, 0, 0);

  // เส้นคั่น
  ctx.fillStyle = "#E5E7EB";
  ctx.fillRect(mainCanvas.width, 0, 1, mainCanvas.height);

  // === ฟอนต์ของ panel อิงจากความสูงภาพ ให้ใหญ่ขึ้นแบบอ่านง่าย ===
  const H = mainCanvas.height;
  const titleFont = Math.max(18, Math.round(H * 0.035)); // หัวข้อ "ตัวกรอง"
  const itemFont  = Math.max(16, Math.round(H * 0.024)); // รายการ {cls} (count)
  const itemGap   = Math.max(22, Math.round(H * 0.022));

  const startX = mainCanvas.width + 20;
  let y = 30 + titleFont;

  // หัวข้อ
  ctx.fillStyle = "#111827";
  ctx.font = `bold ${titleFont}px Arial`;
  ctx.fillText("ผลการตรวจ", startX, y);

  // ขีดเส้นใต้
  y += Math.round(titleFont * 0.4);
  ctx.fillStyle = "#E5E7EB";
  ctx.fillRect(startX, y, panelWidth - 40, 2);
  y += Math.round(titleFont * 0.9);

  // รายการ
  ctx.font = `${itemFont}px Arial`;
  if (hasLines) {
    lines.forEach(({ cls, count, color }) => {
      // ช่องสี
      const boxSize = Math.max(12, Math.round(itemFont * 0.9));
      ctx.fillStyle = color;
      ctx.fillRect(startX, y - boxSize + 2, boxSize, boxSize);

      // ข้อความ
      ctx.fillStyle = "#111827";
      ctx.fillText(`${cls} (${count} จุด)`, startX + boxSize + 10, y);

      y += itemGap;
    });
  } else {
    ctx.fillStyle = "#6B7280";
    ctx.fillText("ไม่พบผลตรวจ", startX, y);
  }

  // ดาวน์โหลด
  const baseName = selectedFile?.name?.replace(/\.[^/.]+$/, "") || "result";
  const fileName = `${baseName}-detected.png`;

  const url = exportCanvas.toDataURL("image/png");
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

  /*const handleDownload = () => {
    const mainCanvas = canvasRef.current;
    if (!mainCanvas) return;

    // Prepare summary lines from the visibleClasses & classCounts
    const lines = Object.keys(visibleClasses)
      .filter((cls) => visibleClasses[cls] && (classCounts[cls] || 0) > 0)
      .map((cls) => ({ cls, count: classCounts[cls], color: classColors[cls] || "black" }));

    // At least show something even if nothing visible
    const hasLines = lines.length > 0;

    // Panel width responsive to image size
    const panelWidth = Math.max(220, Math.round(mainCanvas.width * 0.28));
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = mainCanvas.width + panelWidth;
    exportCanvas.height = mainCanvas.height;

    const ctx = exportCanvas.getContext("2d");

    // White background
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

    // Draw the main annotated image
    ctx.drawImage(mainCanvas, 0, 0);

    // Draw a subtle separator line between image and panel
    ctx.fillStyle = "#E5E7EB";
    ctx.fillRect(mainCanvas.width, 0, 1, exportCanvas.height);

    // Padding and fonts for the right panel
    const startX = mainCanvas.width + 20;
    let y = 30;

    // Title
    ctx.fillStyle = "#111827";
    ctx.font = "bold 22px Arial";
    ctx.fillText("ผลตรวจ", startX, y);
    y += 16;

    // underline
    y += 8;
    ctx.fillStyle = "#E5E7EB";
    ctx.fillRect(startX, y, panelWidth - 40, 2);
    y += 24;

    // Lines
    ctx.font = "16px Arial";
    if (hasLines) {
      lines.forEach(({ cls, count, color }) => {
        // color square
        ctx.fillStyle = color;
        ctx.fillRect(startX, y - 12, 14, 14);

        // text
        ctx.fillStyle = "#111827";
        ctx.fillText(`${cls} (${count} จุด)`, startX + 22, y);
        y += 26;
      });
    } else {
      ctx.fillStyle = "#6B7280";
      ctx.fillText("ไม่พบผลตรวจ", startX, y);
    }

    // Compose filename
    const baseName = selectedFile?.name?.replace(/\.[^/.]+$/, "") || "result";
    const fileName = `${baseName}-detected.png`;

    // Download
    const url = exportCanvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  */

  /**
   * Clear everything
   */
  const handleClear = () => {
    setSelectedFile(null);
    setDetectionResults(null);
    setError(null);
    setIsLoading(false);
    setClassColors({});
    setVisibleClasses({});
    setClassCounts({});

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  return (
    <VStack spacing={6} align="center" w="full" py={8} px={4}>
      {/* Header */}
      <Box textAlign="center">
        <Text fontSize="2xl" fontWeight="bold" mb={2} color="teal.600">
          {title}
        </Text>
        <Text fontSize="md" color="gray.500">
          Upload an image and let the system detect.
        </Text>
      </Box>

      {/* File Input */}
      <Input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        disabled={isLoading}
        variant="filled"
        focusBorderColor="teal.400"
      />

      {/* Error Alert */}
      {error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Buttons */}
      <HStack spacing={4}>
        <Button
          colorScheme="teal"
          onClick={handleUpload}
          isLoading={isLoading}
          loadingText="Processing..."
          isDisabled={!selectedFile || isLoading}
          size="lg"
          shadow="md"
        >
          Upload and Detect
        </Button>

        <Button
          colorScheme="orange"
          onClick={handleClear}
          isDisabled={isLoading}
          size="lg"
          shadow="md"
        >
          Clear
        </Button>

        <Button
          colorScheme="blue"
          onClick={handleDownload}
          isDisabled={!detectionResults || isLoading}
          size="lg"
          shadow="md"
        >
          ดาวน์โหลดภาพผลตรวจ
        </Button>
      </HStack>

      {/* Loading Spinner */}
      {isLoading && <Spinner size="xl" thickness="4px" color="teal.500" />}

      {/* Canvas + Filter Panel */}
      {selectedFile && (
        <Flex
          mt={6}
          justify="center"
          align="start"
          gap={6}
          direction={["column", "row"]}
          w="full"
        >
          {/* Canvas for image + bounding boxes */}
          {detectionResults && (
            <Box flex="1" textAlign="center">
              <Text fontSize="lg" fontWeight="bold" color="teal.600" mb={4}>
                Detection Results
              </Text>
              <canvas
                ref={canvasRef}
                style={{ border: "1px solid #ccc", maxWidth: "100%" }}
              />
            </Box>
          )}

          {/* Class Filter Panel */}
          {Object.keys(visibleClasses).length > 0 && (
            <Box flex="0.3" p={4} border="1px solid #ddd" borderRadius="md">
              <Text fontSize="lg" mb={2} fontWeight="bold">
                ตัวกรอง
              </Text>
              {Object.keys(visibleClasses).map((cls) => (
                <HStack key={cls} mb={2}>
                  <Checkbox
                    isChecked={visibleClasses[cls]}
                    onChange={() => toggleClassVisibility(cls)}
                    colorScheme="teal"
                  >
                    <Text fontWeight="semibold" color={classColors[cls]}>
                      {cls} ({classCounts[cls] || 0})
                    </Text>
                  </Checkbox>
                </HStack>
              ))}
            </Box>
          )}
        </Flex>
      )}
    </VStack>
  );
};

export default ImageDetectionComponent;
